class RateLimitError extends Error {
  constructor(message = "Too many requests. Please wait a moment before trying again.") {
    super(message);
    this.name = "RateLimitError";
  }
}
const DEFAULT_API_BASE_URL = "/api-rewards";
const streamApi = async ({
  prompt,
  base64Image,
  imageMimeType,
  imageUrl,
  brandId,
  appConfigId,
  onChunk,
  onEvent,
  onStatusChange,
  onComplete,
  onError,
  conversationId,
  onConversationId,
  onNewProductMetadata,
  onFeaturedProduct,
  onStoreLocator,
  onOrders,
  onSurveys,
  headersToInject,
  apiBaseUrl = DEFAULT_API_BASE_URL,
  devOptions,
  signal
}) => {
  var _a, _b, _c;
  try {
    const headers = {
      ...headersToInject,
      Accept: "text/event-stream",
      "Content-Type": "application/json"
    };
    const response = await fetch(`${apiBaseUrl}/concierge/agent/stream`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        brandId,
        appConfigId,
        prompt,
        imageData: base64Image,
        imageMimeType,
        imageUrl,
        conversationId,
        ...devOptions !== void 0 && { devOptions }
      }),
      signal
    });
    if (!response.ok) {
      if (response.status === 429) {
        throw new RateLimitError();
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const reader = (_a = response.body) == null ? void 0 : _a.getReader();
    if (!reader) {
      throw new Error("No reader available");
    }
    const decoder = new TextDecoder();
    let buffer = "";
    streamLoop: while (true) {
      const { done, value } = await reader.read();
      if (done) {
        onComplete == null ? void 0 : onComplete();
        break;
      }
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        try {
          let jsonLine = line;
          if (line.startsWith("data: ")) {
            jsonLine = line.slice(6);
          }
          if (!jsonLine.trim()) {
            continue;
          }
          const message = JSON.parse(jsonLine);
          switch (message.type) {
            case "conversation_id":
              onConversationId == null ? void 0 : onConversationId(message.conversation_id);
              break;
            case "status":
              onStatusChange(message.message || "");
              break;
            case "content":
              const content = message.content || "";
              onChunk(content);
              break;
            case "products":
              const productMetadata = message.content || [];
              onNewProductMetadata == null ? void 0 : onNewProductMetadata(productMetadata);
              onChunk(JSON.stringify(productMetadata));
              break;
            case "featured_product":
              if (message.type === "featured_product") {
                onFeaturedProduct == null ? void 0 : onFeaturedProduct(message.content, message.question);
              }
              break;
            case "follow_up_queries":
              onEvent({
                type: "follow_up_queries",
                name: "follow_up_queries",
                data: message.content || []
              });
              break;
            case "store_locator":
              if (message.type === "store_locator" && ((_b = message.data) == null ? void 0 : _b.stacklineSku)) {
                onStoreLocator == null ? void 0 : onStoreLocator(message.data.stacklineSku);
              }
              break;
            case "orders":
              if (message.type === "orders" && message.content) {
                onOrders == null ? void 0 : onOrders(message.content);
              }
              break;
            case "surveys":
              if (message.type === "surveys" && message.content) {
                onSurveys == null ? void 0 : onSurveys(message.content);
              }
              break;
            case "event":
              onEvent(message);
              break;
            case "v2:section_header":
              onEvent({ type: "event", name: "v2:section_header", data: message.data });
              break;
            case "v2:product":
              onEvent({ type: "event", name: "v2:product", data: message.data });
              if ((_c = message.data) == null ? void 0 : _c.product) {
                onNewProductMetadata == null ? void 0 : onNewProductMetadata([message.data.product]);
              }
              break;
            case "v2:sections_complete":
              onEvent({ type: "event", name: "v2:sections_complete" });
              break;
            case "end":
              onChunk("\n\n");
              break;
            case "complete":
              onStatusChange("");
              onComplete == null ? void 0 : onComplete();
              break streamLoop;
            default:
              console.warn("Unknown message type:", message);
              break;
          }
        } catch (parseError) {
          console.warn("Failed to parse line as JSON, skipping:", line);
          continue;
        }
      }
    }
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    } else {
      onError == null ? void 0 : onError(error);
    }
  }
};
const USE_HARDCODED_CONVERSATION = false;
const loadConversation = async ({
  conversationId,
  headersToInject,
  apiBaseUrl = DEFAULT_API_BASE_URL
}) => {
  if (!conversationId) {
    return null;
  }
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const response = await fetch(`${apiBaseUrl}/concierge/conversation/${conversationId}`, {
      method: "GET",
      headers
    });
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      if (response.status === 403) {
        const data2 = await response.json();
        return { error: "unauthorized", redirect: data2.redirect };
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (data.error) {
      console.error("Error loading conversation:", data.error);
      return null;
    }
    return data;
  } catch (error) {
    console.error("Failed to load conversation:", error);
    return null;
  }
};
const sendMessageFeedback = async ({
  conversationId,
  messageIndex,
  feedbackType,
  headersToInject,
  apiBaseUrl = DEFAULT_API_BASE_URL
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(`${apiBaseUrl}/concierge/message/feedback`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      conversationId,
      messageIndex,
      feedbackType
    })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};
const setConversationSharing = async ({
  conversationId,
  enabled,
  messageIndex,
  headersToInject,
  apiBaseUrl = DEFAULT_API_BASE_URL
}) => {
  const headers = {
    ...headersToInject,
    "Content-Type": "application/json"
  };
  const response = await fetch(`${apiBaseUrl}/concierge/conversation/${conversationId}/share`, {
    method: "POST",
    headers,
    body: JSON.stringify({ enabled, messageIndex })
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};
const fetchDevOptions = async ({
  headersToInject,
  apiBaseUrl = DEFAULT_API_BASE_URL
} = {}) => {
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const response = await fetch(`${apiBaseUrl}/concierge/devOptions`, {
      method: "GET",
      headers
    });
    if (!response.ok) {
      console.warn("Failed to fetch dev options from server, using local defaults");
      return null;
    }
    return await response.json();
  } catch (error) {
    console.warn("Error fetching dev options:", error);
    return null;
  }
};
const fetchProductByStacklineSku = async ({
  stacklineSku,
  headersToInject,
  apiBaseUrl = DEFAULT_API_BASE_URL
}) => {
  try {
    const headers = {
      ...headersToInject,
      "Content-Type": "application/json"
    };
    const response = await fetch(`${apiBaseUrl}/product/${stacklineSku}`, {
      method: "GET",
      headers
    });
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch product: ${response.status}`);
    }
    const data = await response.json();
    return data.product || data;
  } catch (error) {
    console.error("Error fetching product by stacklineSku:", error);
    return null;
  }
};
export {
  RateLimitError,
  USE_HARDCODED_CONVERSATION,
  fetchDevOptions,
  fetchProductByStacklineSku,
  loadConversation,
  sendMessageFeedback,
  setConversationSharing,
  streamApi
};
//# sourceMappingURL=api.js.map
